/**
 * AdminBillingPanel — Coûts de la plateforme
 *  • KPIs usage interne du mois (générations / matérialthèque / emails / storage)
 *  • Tableau des factures externes saisies manuellement (CRUD)
 *  • Édition des coûts unitaires (config singleton)
 *  • Sélecteur mois/année
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Wand2, Camera, FileText, Mail, HardDrive, Database, Cloud,
  Plus, Trash2, Edit3, X, Loader2, Receipt, Calculator, TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINE_META = {
  shoebox:         { label: 'Shoebox générées',     icon: Wand2,      color: '#D9A05B', unit: '€/img' },
  fps:             { label: 'FPS générées',         icon: Camera,     color: '#14B8A6', unit: '€/img' },
  materialtheque:  { label: 'Matériauthèque IA',    icon: FileText,   color: '#A855F7', unit: '€/PDF' },
  emails:          { label: 'Emails envoyés',       icon: Mail,       color: '#3B82F6', unit: '€/email' },
  storage:         { label: 'Stockage (Go)',        icon: HardDrive,  color: '#22C55E', unit: '€/Go' },
  mongo_fixed:     { label: 'MongoDB Atlas (fixe)', icon: Database,   color: '#10B981', unit: '€/mois' },
  cloud_run_fixed: { label: 'Cloud Run (fixe)',     icon: Cloud,      color: '#0EA5E9', unit: '€/mois' },
};

const CATEGORIES = [
  { id: 'llm', label: 'LLM / API IA (Emergent, OpenAI, Gemini)' },
  { id: 'maps', label: 'Google Maps / Cartographie' },
  { id: 'storage', label: 'Stockage cloud' },
  { id: 'email', label: 'Email / Notifications (Resend, SendGrid)' },
  { id: 'infra', label: 'Infrastructure (Cloud Run, MongoDB, GCS)' },
  { id: 'payments', label: 'Paiements (Stripe)' },
  { id: 'other', label: 'Autre' },
];

export default function AdminBillingPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/billing/usage?year=${year}&month=${month}`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      toast.error('Erreur chargement coûts');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#D9A05B' }} />
      </div>
    );
  }
  if (!data) return null;

  const monthsList = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
  ];
  const yearsList = [now.getFullYear(), now.getFullYear() - 1];

  return (
    <div className="space-y-6" data-testid="admin-billing-panel">
      {/* Header with month selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-600" />
            Coûts plateforme
          </h2>
          <p className="text-sm text-gray-500">
            Usage interne tracké automatiquement · Factures externes saisies manuellement
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-36" data-testid="billing-month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthsList.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-24" data-testid="billing-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearsList.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfigDialog(true)}
            data-testid="billing-edit-config"
          >
            <Calculator className="w-4 h-4 mr-1.5" />
            Coûts unitaires
          </Button>
        </div>
      </div>

      {/* Total card */}
      <div
        className="rounded-xl p-5"
        style={{
          background: 'linear-gradient(135deg, rgba(217,160,91,0.10) 0%, rgba(217,160,91,0.04) 100%)',
          border: '1px solid rgba(217,160,91,0.30)',
        }}
        data-testid="billing-total-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-amber-700 font-semibold">
              Total estimé · {monthsList[month - 1]} {year}
            </p>
            <p className="text-3xl font-bold text-gray-900 mt-1 tabular-nums">
              {data.total_eur.toFixed(2)} €
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Interne {data.total_estimated_eur.toFixed(2)} € + Factures {data.total_invoices_eur.toFixed(2)} €
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="tabular-nums">{data.active_users} utilisateurs actifs</span>
          </div>
        </div>
      </div>

      {/* Internal usage grid */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">📊 Usage interne tracké</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(data.cost_lines).map(([k, v]) => {
            const meta = LINE_META[k] || {};
            const Icon = meta.icon || FileText;
            const isFixed = k.endsWith('_fixed');
            return (
              <div
                key={k}
                className="bg-white rounded-xl border border-gray-200 p-3"
                data-testid={`billing-line-${k}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}44` }}
                  >
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">{meta.label || k}</p>
                    <p className="text-[10px] text-gray-400">{v.unit_eur} {meta.unit}</p>
                  </div>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-[10px] text-gray-500">
                    {isFixed ? '—' : `${v.count.toLocaleString('fr-FR')}`}
                  </span>
                  <span className="text-lg font-bold tabular-nums" style={{ color: meta.color }}>
                    {v.total_eur.toFixed(2)} €
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* External invoices */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            🧾 Factures externes ({data.invoices.length})
          </h3>
          <Button
            size="sm"
            onClick={() => setShowInvoiceDialog(true)}
            data-testid="billing-add-invoice"
            style={{ background: '#D9A05B', color: '#000' }}
            className="hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter une facture
          </Button>
        </div>
        {data.invoices.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
            <Receipt className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Aucune facture saisie pour ce mois</p>
            <p className="text-xs text-gray-400 mt-1">
              Saisis les factures Emergent, Google Cloud, MongoDB Atlas, Resend…
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Fournisseur</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Catégorie</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Date</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Montant</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.invoices.map((inv) => (
                  <tr key={inv.invoice_id} data-testid={`billing-invoice-row-${inv.invoice_id}`}>
                    <td className="px-3 py-2 font-medium text-gray-900">{inv.provider}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {CATEGORIES.find((c) => c.id === inv.category)?.label || inv.category}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{inv.invoice_date}</td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">
                      {inv.amount_eur.toFixed(2)} €
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={async () => {
                          if (!window.confirm(`Supprimer la facture ${inv.provider} ?`)) return;
                          try {
                            await axios.delete(`${API}/admin/billing/invoices/${inv.invoice_id}`, {
                              withCredentials: true,
                            });
                            toast.success('Facture supprimée');
                            load();
                          } catch {
                            toast.error('Erreur suppression');
                          }
                        }}
                        className="h-7 w-7 hover:bg-red-50"
                        data-testid={`billing-invoice-delete-${inv.invoice_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showInvoiceDialog && (
        <NewInvoiceDialog
          year={year}
          month={month}
          onClose={() => setShowInvoiceDialog(false)}
          onCreated={() => { setShowInvoiceDialog(false); load(); }}
        />
      )}
      {showConfigDialog && (
        <ConfigDialog
          config={data.config}
          onClose={() => setShowConfigDialog(false)}
          onSaved={() => { setShowConfigDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function NewInvoiceDialog({ year, month, onClose, onCreated }) {
  const [provider, setProvider] = useState('');
  const [category, setCategory] = useState('llm');
  const [amount, setAmount] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(`${year}-${String(month).padStart(2, '0')}-01`);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!provider || !amount) {
      toast.error('Fournisseur et montant requis');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/billing/invoices`,
        {
          provider,
          category,
          amount_eur: parseFloat(amount),
          year,
          month,
          invoice_date: invoiceDate,
          notes,
        },
        { withCredentials: true }
      );
      toast.success('Facture ajoutée');
      onCreated();
    } catch (e) {
      toast.error('Erreur ajout facture');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Nouvelle facture externe</DialogTitle>
          <DialogDescription>
            Saisie manuelle d'une facture reçue (Emergent, Google, MongoDB, Resend, etc.)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Fournisseur *</Label>
            <Input
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              placeholder="Ex: Emergent / Google Cloud / MongoDB Atlas"
              data-testid="invoice-provider"
            />
          </div>
          <div>
            <Label>Catégorie</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="invoice-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Montant (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                data-testid="invoice-amount"
              />
            </div>
            <div>
              <Label>Date de facture</Label>
              <Input
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                data-testid="invoice-date"
              />
            </div>
          </div>
          <div>
            <Label>Notes (optionnel)</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ref invoice / commentaire"
              data-testid="invoice-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="invoice-save"
            style={{ background: '#D9A05B', color: '#000' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigDialog({ config, onClose, onSaved }) {
  const [values, setValues] = useState({ ...config });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.keys(values).forEach((k) => {
        const num = parseFloat(values[k]);
        if (!isNaN(num)) payload[k] = num;
      });
      await axios.put(`${API}/admin/billing/config`, payload, { withCredentials: true });
      toast.success('Coûts unitaires mis à jour');
      onSaved();
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="config-dialog">
        <DialogHeader>
          <DialogTitle>Coûts unitaires</DialogTitle>
          <DialogDescription>
            Ajuste ces valeurs pour refléter tes vrais coûts (factures fournisseurs / 1000 unités).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {Object.entries(values).filter(([k]) => k in LINE_META || ['maps_per_1k_tiles_eur'].includes(k)).map(([k, v]) => {
            const meta = LINE_META[k] || { label: k.replace(/_/g, ' '), color: '#64748B', unit: '€' };
            return (
              <div key={k} className="flex items-center gap-2">
                <Label className="flex-1 text-xs">{meta.label} ({meta.unit})</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={v}
                  onChange={(e) => update(k, e.target.value)}
                  className="w-32 text-xs"
                  data-testid={`config-${k}`}
                />
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="config-save"
            style={{ background: '#D9A05B', color: '#000' }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
